import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Moon, Sparkles, Loader2, History } from "lucide-react";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { NightCard } from "../components/NightCard";
import { RescheduleDialog } from "../components/RescheduleDialog";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

function toPlan(night) {
  return {
    city: night.city,
    date: night.date,
    budget: night.budget,
    vibe: night.vibe,
    event_found: night.event_found,
    stops: night.stops,
    city_center: night.city_center || (night.stops[0] ? { lat: night.stops[0].lat, lon: night.stops[0].lon } : null),
    candidates: night.candidates || { dinner: [], drinks: [] },
  };
}

async function copyLink(shareId) {
  const url = `${window.location.origin}/share/${shareId}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Share link copied to clipboard!", { description: url });
  } catch {
    toast.success("Share link ready", { description: url, duration: 10000 });
  }
}

export default function MyNights() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [nights, setNights] = useState(null);
  const [shares, setShares] = useState({});
  const [busy, setBusy] = useState({});
  const [tab, setTab] = useState("upcoming");
  const [rescheduling, setRescheduling] = useState(null);
  const [rescheduleBusy, setRescheduleBusy] = useState(false);

  useEffect(() => {
    if (ready && !user) navigate("/auth");
  }, [ready, user, navigate]);

  const refreshShare = useCallback(
    (shareId) =>
      api.get(`/share/${shareId}`).then(({ data }) => setShares((s) => ({ ...s, [shareId]: data }))).catch(() => {}),
    []
  );

  useEffect(() => {
    if (!user) return;
    api
      .get("/nights")
      .then(({ data }) => {
        setNights(data);
        data.filter((n) => n.share_id).forEach((n) => refreshShare(n.share_id));
      })
      .catch(() => setNights([]));
  }, [user, refreshShare]);

  const setNightBusy = (id, state) => setBusy((b) => ({ ...b, [id]: state }));
  const replaceNight = (updated) => setNights((list) => list.map((n) => (n.id === updated.id ? { ...n, ...updated } : n)));

  const today = todayISO();
  const { upcoming, past } = useMemo(() => {
    const list = nights || [];
    return {
      upcoming: list.filter((n) => n.date >= today).sort((a, b) => a.date.localeCompare(b.date)),
      past: list.filter((n) => n.date < today).sort((a, b) => b.date.localeCompare(a.date)),
    };
  }, [nights, today]);
  const visible = tab === "upcoming" ? upcoming : past;

  const remove = async (id) => {
    try {
      await api.delete(`/nights/${id}`);
      setNights((n) => n.filter((x) => x.id !== id));
      toast.success("Night removed.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  const view = (night) => {
    const plan = toPlan(night);
    sessionStorage.setItem("nightout-plan", JSON.stringify(plan));
    navigate("/itinerary", { state: { plan } });
  };

  const share = async (night) => {
    setNightBusy(night.id, "share");
    try {
      const { data } = await api.post(`/nights/${night.id}/share`);
      replaceNight({ id: night.id, share_id: data.id });
      refreshShare(data.id);
      await copyLink(data.id);
    } catch {
      toast.error("Could not create share link.");
    } finally {
      setNightBusy(night.id, null);
    }
  };

  const applyVote = async (night, stopType) => {
    setNightBusy(night.id, `apply:${stopType}`);
    try {
      const { data } = await api.post(`/nights/${night.id}/apply-vote`, { stop_type: stopType });
      replaceNight(data);
      await refreshShare(night.share_id);
      const stop = data.stops.find((s) => s.type === stopType);
      toast.success(`Crowd pick applied: ${stop?.name}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not apply the vote.");
    } finally {
      setNightBusy(night.id, null);
    }
  };

  const reschedule = async (date) => {
    setRescheduleBusy(true);
    try {
      const { data } = await api.post(`/nights/${rescheduling.id}/reschedule`, { date });
      replaceNight(data);
      if (data.share_id) refreshShare(data.share_id);
      toast.success(
        data.event_found ? `Moved to ${date} — new live event lined up.` : `Moved to ${date}. No live events that night, so it's dinner & drinks.`
      );
      setRescheduling(null);
      setTab(date >= today ? "upcoming" : "past");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reschedule.");
    } finally {
      setRescheduleBusy(false);
    }
  };

  const TabButton = ({ id, label, count }) => (
    <button
      data-testid={`nights-tab-${id}`}
      onClick={() => setTab(id)}
      className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
        tab === id ? "bg-foreground text-background" : "bg-secondary/40 text-muted-foreground hover:text-foreground"
      }`}
    >
      {label} <span className={`ml-1.5 text-xs ${tab === id ? "text-background/70" : "text-muted-foreground"}`}>{count}</span>
    </button>
  );

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">My Nights</h1>
            <p className="mt-2 text-muted-foreground">Your saved plans, ready to relive.</p>
          </div>
          {nights && nights.length > 0 && (
            <div className="flex gap-2" data-testid="nights-tabs">
              <TabButton id="upcoming" label="Upcoming" count={upcoming.length} />
              <TabButton id="past" label="Past" count={past.length} />
            </div>
          )}
        </div>

        {nights === null ? (
          <div className="mt-16 flex justify-center text-muted-foreground">
            <Loader2 className="animate-spin" />
          </div>
        ) : nights.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary">
              <Moon size={28} className="neon-magenta" />
            </span>
            <p className="mt-4 text-lg font-semibold">No saved nights yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Plan a night and hit save to see it here.</p>
            <Button data-testid="empty-plan-btn" onClick={() => navigate("/")} className="mt-6 rounded-full cta-gradient text-white font-semibold">
              <Sparkles size={16} className="mr-2" /> Plan a night
            </Button>
          </div>
        ) : visible.length === 0 ? (
          <div className="mt-16 flex flex-col items-center text-center text-muted-foreground" data-testid="nights-tab-empty">
            <History size={28} className="neon-cyan" />
            <p className="mt-4 text-sm">
              {tab === "upcoming" ? "Nothing coming up — plan your next night out." : "No past nights yet."}
            </p>
          </div>
        ) : (
          <div data-testid="saved-nights-grid" className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((n, i) => (
              <NightCard
                key={n.id}
                night={n}
                index={i}
                share={n.share_id ? shares[n.share_id] : null}
                busy={busy[n.id]}
                onView={view}
                onShare={share}
                onReschedule={setRescheduling}
                onDelete={remove}
                onApplyVote={applyVote}
                toPlan={toPlan}
              />
            ))}
          </div>
        )}
      </main>

      {rescheduling && (
        <RescheduleDialog
          key={rescheduling.id}
          night={rescheduling}
          open
          onOpenChange={(o) => !o && !rescheduleBusy && setRescheduling(null)}
          onConfirm={reschedule}
          busy={rescheduleBusy}
        />
      )}
    </>
  );
}
